import React from "react";
import { Box, Typography, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useMetricsUi, DeltaPill } from "./metricsUi";

const formatValue = (metric) => {
  if (metric.status === "unavailable" || metric.status === "empty") {
    return "—";
  }
  if (metric.value == null) return "—";
  const unit = metric.id;
  if (unit === "gross_aov" || unit === "aur") {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(metric.value);
  }
  if (
    unit === "gross_sales" ||
    unit === "net_sales" ||
    unit === "discounts" ||
    unit === "gross_margin_dollars"
  ) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(metric.value);
  }
  if (unit === "upt" || unit === "conversion_rate" || unit === "sell_through") {
    if (unit === "conversion_rate" || unit === "sell_through") {
      return `${(Number(metric.value) * 100).toFixed(1)}%`;
    }
    return Number(metric.value).toFixed(2);
  }
  return new Intl.NumberFormat("en-US").format(metric.value);
};

/**
 * Executive KPI card: muted label, large tabular value, delta pill.
 * Handles ok / zero / empty / unavailable states.
 */
const MetricKpiCard = ({ metric, priorLabel }) => {
  const ui = useMetricsUi();
  if (!metric) return null;

  const isUnavailable = metric.status === "unavailable";
  const isEmpty = metric.status === "empty";

  return (
    <Box
      sx={{
        ...ui.card,
        padding: "14px 16px 13px",
        minHeight: 104,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        opacity: isUnavailable ? 0.85 : 1,
        transition: "border-color .12s, box-shadow .12s",
        "&:hover": {
          borderColor: ui.borderStrong,
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.75 }}>
        <Typography
          sx={{
            fontSize: 11.5,
            fontWeight: 600,
            color: ui.colors.textSecondary,
            letterSpacing: "0.02em",
            lineHeight: 1.3,
          }}
        >
          {metric.display_name}
        </Typography>
        {metric.tooltip && (
          <Tooltip title={metric.tooltip} arrow>
            <InfoOutlinedIcon
              sx={{ fontSize: 13, color: ui.colors.textTertiary }}
            />
          </Tooltip>
        )}
      </Box>

      <Typography
        sx={{
          fontSize: isUnavailable ? 20 : 24,
          fontWeight: 650,
          letterSpacing: "-0.01em",
          fontVariantNumeric: "tabular-nums",
          color: isUnavailable
            ? ui.colors.textTertiary
            : ui.colors.textPrimary,
          lineHeight: 1.15,
        }}
      >
        {formatValue(metric)}
      </Typography>

      <Box sx={{ mt: "auto", pt: 0.9, minHeight: 20 }}>
        {isUnavailable && (
          <Typography
            sx={{ fontSize: 11, color: ui.colors.textTertiary, lineHeight: 1.35 }}
          >
            {metric.reason || "Required data is not available"}
          </Typography>
        )}
        {isEmpty && (
          <Typography sx={{ fontSize: 11.5, color: ui.colors.textTertiary }}>
            No data in selected period
          </Typography>
        )}
        {metric.status === "zero" && (
          <Typography sx={{ fontSize: 11.5, color: ui.colors.textTertiary }}>
            Zero for selected period
          </Typography>
        )}
        {metric.status === "ok" &&
          (metric.comparison ? (
            <DeltaPill comparison={metric.comparison} vsLabel={priorLabel} />
          ) : metric.meta?.note && metric.id === "inventory_on_hand_current" ? (
            <Typography sx={{ fontSize: 11, color: ui.colors.textTertiary }}>
              {metric.meta.note}
            </Typography>
          ) : null)}
      </Box>
    </Box>
  );
};

export default MetricKpiCard;
